/**
 * What the signed-in shell may open, decided from `GET /me/permissions`:
 * first the product's state, then whether this company is one the person
 * holds Link Accounting access to. Pure, so the decision is testable.
 */
import type { components } from "@linkhr/api-client/core";

type MePermissionsView = components["schemas"]["MePermissionsView"];
type CapabilityCompany = components["schemas"]["CapabilityCompany"];
type ProductState = components["schemas"]["ProductState"];

export type Gate =
  | { kind: "product_off"; state: Extract<ProductState, "NOT_SUBSCRIBED" | "LOCKED" | "PAUSED"> }
  | { kind: "no_company" }
  | { kind: "read_only"; company: CapabilityCompany }
  /** Payment overdue: everything still works, with a warning. */
  | { kind: "past_due"; company: CapabilityCompany }
  | { kind: "trial"; until: string | null; company: CapabilityCompany }
  | { kind: "ok"; company: CapabilityCompany };

const hasAccounting = (company: CapabilityCompany): boolean =>
  company.grants.ACCOUNTING !== undefined;

export function gateFor(permissions: MePermissionsView, companyId: string): Gate {
  const { state, stateUntil } = permissions.products.ACCOUNTING;
  if (state === "NOT_SUBSCRIBED" || state === "LOCKED" || state === "PAUSED") {
    return { kind: "product_off", state };
  }
  const company = permissions.companies.find((c) => c.id === companyId && hasAccounting(c));
  if (company === undefined) return { kind: "no_company" };
  if (state === "READ_ONLY") return { kind: "read_only", company };
  if (state === "PAST_DUE") return { kind: "past_due", company };
  if (state === "TRIAL") return { kind: "trial", until: stateUntil, company };
  return { kind: "ok", company };
}

/**
 * The company the app opens on: the default company when the person holds
 * Link Accounting there, else the first one they do, else none. Only ever a
 * company `gateFor` lets in, so opening it can't bounce back here.
 */
export function homeCompanyId(permissions: MePermissionsView): string | null {
  const companies = accountingCompanies(permissions);
  const preferred = companies.find((c) => c.id === permissions.defaultCompanyId);
  return (preferred ?? companies[0])?.id ?? null;
}

/** The account owner holds the company's books by ownership, and alone manages who else does. */
export const ownsCompany = (company: CapabilityCompany): boolean =>
  company.grants.ACCOUNTING?.source === "OWNER";

/** Does the person hold this permission code (e.g. `accounting:journal:post`) on the company's books? */
export const holds = (company: CapabilityCompany, code: string): boolean =>
  company.grants.ACCOUNTING?.permissions.includes(code) === true;

/** The companies the person holds Link Accounting access to (the company switcher's list). */
export function accountingCompanies(permissions: MePermissionsView): CapabilityCompany[] {
  return permissions.companies.filter(hasAccounting);
}
